import { api } from './api';

/**
 * The bug pool.
 *
 * Every mutation returns the whole updated bug rather than a status code, because
 * fading, age, and which bucket a bug now belongs in are all computed server-side
 * from the fade settings. Recomputing them here would mean two copies of the rules.
 */

/**
 * @param {string} organizationId
 * @param {Object} options - { bucket, assigneeUserId }
 */
export const getBugs = async (organizationId, options = {}) => {
  const res = await api.get('/shape-up/bugs', {
    params: { organizationId, ...options },
  });
  return res.data;
};

/**
 * Whether the pool is working: inflow against what each cool-down absorbed, how
 * often the team was interrupted, and how old what nobody picked up has become.
 */
export const getBugAnalytics = async (organizationId) => {
  const res = await api.get('/shape-up/bugs/analytics', { params: { organizationId } });
  return res.data;
};

export const getBug = async (bugId, organizationId) => {
  const res = await api.get(`/shape-up/bugs/${bugId}`, { params: { organizationId } });
  return res.data;
};

/**
 * @param {Object} payload - { title, description, teamId, pushToJira }
 */
export const reportBug = async (organizationId, payload) => {
  const res = await api.post('/shape-up/bugs', { organizationId, ...payload });
  return res.data;
};

/**
 * Edit, triage, or assign. Triage means setting a disposition, which is a decision
 * rather than a priority; declaring an emergency goes through declareEmergency
 * instead, since that one has to name the bet it interrupts.
 *
 * @param {Object} updates - { title, description, disposition, dispositionNote, assigneeUserId }
 */
export const updateBug = async (bugId, organizationId, updates = {}) => {
  const res = await api.patch(`/shape-up/bugs/${bugId}`, { organizationId, ...updates });
  return res.data;
};

export const deleteBug = async (bugId, organizationId) => {
  const res = await api.delete(`/shape-up/bugs/${bugId}`, { data: { organizationId } });
  return res.data;
};

/**
 * @param {number} [emergencyCostDays] - what the interruption actually cost, asked
 *   for at resolution because nobody knows at the point of declaring
 */
export const fixBug = async (bugId, organizationId, emergencyCostDays) => {
  const res = await api.post(`/shape-up/bugs/${bugId}/fix`, {
    organizationId,
    ...(emergencyCostDays !== undefined ? { emergencyCostDays } : {}),
  });
  return res.data;
};

/**
 * Turn a bug too big for cool-down into a pitch it has to win a bet with.
 */
export const escalateBug = async (bugId, organizationId, payload = {}) => {
  const res = await api.post(`/shape-up/bugs/${bugId}/escalate`, { organizationId, ...payload });
  return res.data;
};

/**
 * Naming the interrupted bet is required. Returns the cycle's running emergency
 * count and budget so the caller can say "this is the fourth this cycle".
 */
export const declareEmergency = async (bugId, organizationId, { interruptedBetId, reason }) => {
  const res = await api.post(`/shape-up/bugs/${bugId}/emergency`, {
    organizationId, interruptedBetId, reason,
  });
  return res.data;
};

export const reportBugAgain = async (bugId, organizationId) => {
  const res = await api.post(`/shape-up/bugs/${bugId}/report-again`, { organizationId });
  return res.data;
};

export const sweepBugs = async (organizationId) => {
  const res = await api.post('/shape-up/bugs/sweep', { organizationId });
  return res.data;
};

export const syncBugs = async (organizationId) => {
  const res = await api.post('/shape-up/bugs/sync', { organizationId });
  return res.data;
};

const bugsApi = {
  getBugs,
  getBugAnalytics,
  getBug,
  reportBug,
  updateBug,
  deleteBug,
  fixBug,
  escalateBug,
  declareEmergency,
  reportBugAgain,
  sweepBugs,
  syncBugs,
};

export default bugsApi;
